import React, { useState, useEffect } from 'react';
import { updateProfile, getCurrentUser } from '../../services/authService';
import axios from 'axios';
import { FaUpload, FaTimesCircle, FaCheckCircle } from 'react-icons/fa';
import styles from './Profile.module.css';

// Variables de entorno con fallback
const API_URL = process.env.REACT_APP_API_URL || '';
const ASSETS_URL = process.env.REACT_APP_ASSETS_URL || '';

const Profile = () => {
  const [user, setUser] = useState({});
  const [username, setUsername] = useState('');
  const [nick, setNick] = useState('');
  const [email, setEmail] = useState('');
  const [imgUrl, setImgUrl] = useState(`${ASSETS_URL}/images/avatar.webp`);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [referralUrl, setReferralUrl] = useState('');
  const [referralCode, setReferralCode] = useState('');

  useEffect(() => {
    const userData = getCurrentUser();
    if (userData) {
      setUser(userData);
      setUsername(userData.username || '');
      setNick(userData.nick || '');
      setEmail(userData.email || '');
    const avatarPath =
      process.env.NODE_ENV === 'production'
        ? `${ASSETS_URL}/media/avatars/${userData.img_url}`
        : `${ASSETS_URL}/images/avatars/${userData.img_url}`;

    setImgUrl(
      userData.img_url
        ? avatarPath
        : `${ASSETS_URL}/media/avatar.webp`
    );
      setReferralUrl(`${ASSETS_URL}/register?referrerId=${userData.referral_code || ''}`);
      setReferralCode(userData.referral_code || '');
    }
  }, []);

  const validateImage = (file) => {
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    const maxSize = 128 * 1024; // 128KB
    if (!allowed.includes(file.type)) {
      setErrorMessage('Solo se permiten PNG, JPEG o WebP.');
      return false;
    }
    if (file.size > maxSize) {
      setErrorMessage('El tamaño máximo es 128KB.');
      return false;
    }
    return true; // (Opcionalmente podrías validar dimensiones en servidor)
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && validateImage(file)) {
      setSelectedFile(file);
      setErrorMessage('');
    } else {
      setSelectedFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setUploadProgress(0);

    if (!username || !nick || !email) {
      setErrorMessage('Faltan datos obligatorios.');
      return;
    }

    let avatarFileName = user.img_url || 'avatar.webp';

    if (selectedFile) {
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('oldFileName', avatarFileName);

        const res = await axios.post(`${API_URL}/upload-avatar`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setUploadProgress(pct);
          }
        });

        if (res.data?.fileName) {
          avatarFileName = res.data.fileName;
          setImgUrl(`${ASSETS_URL}/media/avatars/${avatarFileName}`);
          setUser((prev) => ({ ...prev, img_url: avatarFileName }));
          setSuccessMessage('Imagen subida con éxito.');
        } else {
          setErrorMessage('No se recibió el nombre del archivo.');
          return;
        }
      } catch (err) {
        setErrorMessage('Error al subir la imagen.');
        return;
      }
    }

    try {
      const updated = await updateProfile(username, nick, email, avatarFileName);
      if (updated) {
        setUser(updated);
        setSuccessMessage('Perfil actualizado con éxito.');
      } else {
        setErrorMessage('No se pudo actualizar el perfil.');
      }
    } catch {
      setErrorMessage('Error al actualizar el perfil.');
    }
  };

  return (
    <div className={styles.profileContainer}>
      <h2 className={styles.profileTitle}>Actualizar Perfil</h2>

      <form onSubmit={handleSubmit} noValidate>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="username">Nombre de usuario</label>
          <input
            id="username"
            className={styles.input}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="nick">Nick</label>
          <input
            id="nick"
            className={styles.input}
            value={nick}
            onChange={(e) => setNick(e.target.value)}
            autoComplete="nickname"
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="email">Email</label>
          <input
            id="email"
            className={styles.input}
            type="email"
            value={email}
            readOnly
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="avatar">Avatar</label>
          <img src={imgUrl} alt="Avatar" className={styles.avatar} />
          <input id="avatar" className={styles.input} type="file" onChange={handleFileChange} accept="image/png,image/jpeg,image/webp" />
          {uploadProgress > 0 && (
            <div className={styles.progress} role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100}>
              <div className={styles.progressBar} style={{ width: `${uploadProgress}%` }}>
                {uploadProgress}%
              </div>
            </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="refcode">Código de referido</label>
          <input id="refcode" className={styles.input} type="text" value={referralCode} readOnly />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="refurl">URL de referido</label>
          <input id="refurl" className={styles.input} type="text" value={referralUrl} readOnly />
        </div>

        {errorMessage && (
          <div className={styles.alertDanger} role="alert">
            <FaTimesCircle aria-hidden /> {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className={styles.alertSuccess} role="status">
            <FaCheckCircle aria-hidden /> {successMessage}
          </div>
        )}

        <button type="submit" className={styles.btnPrimary}>
          <FaUpload style={{ marginRight: 8 }} aria-hidden /> Actualizar Perfil
        </button>
      </form>
    </div>
  );
};

export default Profile;
